import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, ArrowLeft } from 'lucide-react';
import { usePageContent, getSectionFields } from '../hooks/usePageContent';

/**
 * Generic renderer for static / legal pages whose body lives in MongoDB
 * (page → section type "legal" → field key "content"). The admin v2 page
 * editor controls the content; this component just fetches it and renders
 * the HTML safely inside a typographically-styled wrapper.
 *
 * Accepts a `slug` prop to make routing self-documenting (e.g. <LegalPage
 * slug="privacy-policy" />). Falls back to URL :slug if the prop is missing.
 */
export default function LegalPage({ slug: propSlug }) {
  const params = useParams();
  const slug = propSlug || params.slug;
  const { page, loading } = usePageContent(slug);

  // Accept either a `legal` section (compliance pages) or a `rich_text`
  // section (About / Awards / Reviews / Contact narrative pages). Field
  // naming differs slightly (`title` vs `heading`) — handle both so the
  // same renderer powers every static page.
  const body = getSectionFields(page, 'legal') || getSectionFields(page, 'rich_text') || {};
  const heading = body.title || body.heading || page?.name || 'Page';
  const lastUpdated = body.last_updated || '';
  const content = body.content || '';

  return (
    <section className="bg-gradient-to-b from-violet-50/40 via-white to-white min-h-[60vh]">
      <div className="container-x section-pad max-w-4xl">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-violet-600 mb-6"
        >
          <ArrowLeft size={14} /> Back to home
        </Link>

        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 leading-tight">
            {heading}
          </h1>
          {lastUpdated && (
            <p className="text-sm text-slate-500 mt-3">Last updated: {lastUpdated}</p>
          )}
        </div>

        {loading && !content && (
          <div className="flex items-center justify-center gap-2 text-slate-400 py-12">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        )}

        {!loading && !content && (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-10 text-center text-slate-500">
            <p className="font-semibold">This page has not been published yet.</p>
            <p className="text-sm mt-2">
              The administrator can publish content from{' '}
              <code className="text-violet-600">/admin-v2/pages/{slug}</code>.
            </p>
          </div>
        )}

        {content && (
          <article
            className="legal-prose text-slate-700"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        )}
      </div>

      {/* Minimal typography for legal HTML content — avoids needing the
          @tailwindcss/typography plugin while keeping pages readable. */}
      <style>{`
        .legal-prose h2 { font-size: 1.5rem; font-weight: 800; color: #0f172a; margin-top: 2.25rem; margin-bottom: 0.75rem; }
        .legal-prose h3 { font-size: 1.15rem; font-weight: 700; color: #0f172a; margin-top: 1.5rem; margin-bottom: 0.5rem; }
        .legal-prose p { line-height: 1.7; margin-bottom: 1rem; }
        .legal-prose ul, .legal-prose ol { padding-left: 1.5rem; margin-bottom: 1rem; line-height: 1.7; }
        .legal-prose ul { list-style: disc; }
        .legal-prose ol { list-style: decimal; }
        .legal-prose li { margin-bottom: 0.4rem; }
        .legal-prose a { color: #7c3aed; }
        .legal-prose a:hover { color: #6d28d9; text-decoration: underline; }
        .legal-prose strong { color: #0f172a; font-weight: 700; }
        .legal-prose code { background: #f1f5f9; padding: 0.1rem 0.35rem; border-radius: 0.25rem; font-size: 0.875em; color: #6d28d9; }
      `}</style>
    </section>
  );
}
